import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

const AdCopy = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission check
  const { hasModelAccess, getPlanInfo, getRequiredPlanForModel } = usePermission();

  // Upgrade prompt state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});

  // Mode toggle
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    // Basic fields
    productName: '',
    productDescription: '',
    targetAudience: '',

    // Customization
    tone: 'professional',
    language: 'en-US',

    // Advanced fields
    keywords: '',
    cta: '',
    brandVoice: '',
    painPoints: '',
    benefits: '',
    competitors: '',

    // Generation options
    numVariations: 1,
    includeImages: false,
    maxLength: 'medium'
  });

  // State management
  const [generating, setGenerating] = useState(false);
  const [optimizingDescription, setOptimizingDescription] = useState(false);

  // Show warning when user tries to leave during generation
  const navigationWarning = useNavigationWarning(generating || optimizingDescription, 'Content is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'content-generation');

  const [generatedAds, setGeneratedAds] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);

  // Model display mapping - hide technical names
  const getModelDisplayInfo = (modelName) => {
    const modelMap = {
      'deepseek-chat': {
        displayName: 'Basic - Fast Response',
        tier: 'Basic',
        description: 'Quick and efficient for everyday tasks',
        icon: '⚡',
        badge: 'Basic'
      },
      'deepseek-reasoner': {
        displayName: 'Basic - Deep Thinking',
        tier: 'Basic',
        description: 'Advanced reasoning for complex problems',
        icon: '🧠',
        badge: 'Basic'
      },
      'doubao-seed-1-6': {
        displayName: 'Professional - Vision Pro',
        tier: 'Professional',
        description: 'Image understanding + text generation',
        icon: '👁️',
        badge: 'Pro'
      },
      'qwen3-vl-plus': {
        displayName: 'Professional - Vision Plus',
        tier: 'Professional',
        description: 'Advanced multimodal AI with vision',
        icon: '🎯',
        badge: 'Plus'
      }
    };
    return modelMap[modelName] || {
      displayName: modelName,
      tier: 'Standard',
      description: 'AI model',
      icon: '🤖',
      badge: null
    };
  };

  const tones = [
    {
      value: 'professional',
      label: 'Professional',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M172.8 960a45.568 45.568 0 0 1-31.296-12.8 44.096 44.096 0 0 1-13.44-32V128a64 64 0 0 1 64-64h640a64 64 0 0 1 64 64v787.2a44.8 44.8 0 0 1-60.608 41.728L512 833.92l-323.392 123.008A43.712 43.712 0 0 1 172.8 960z m195.2-512a48 48 0 0 0 0 96h288a48 48 0 1 0 0-96z m0-192a48 48 0 0 0 0 96h288a48 48 0 1 0 0-96z" fill="#13227a"/></svg>
    },
    {
      value: 'casual',
      label: 'Casual & Friendly',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M512 938.666667C276.352 938.666667 85.333333 747.648 85.333333 512S276.352 85.333333 512 85.333333s426.666667 191.018667 426.666667 426.666667-191.018667 426.666667-426.666667 426.666667z m0-85.333334a341.333333 341.333333 0 1 0 0-682.666666 341.333333 341.333333 0 0 0 0 682.666666z m-213.333333-341.333333h85.333333a128 128 0 1 0 256 0h85.333333a213.333333 213.333333 0 0 1-426.666666 0z" fill="#f4ea2a"/></svg>
    },
    {
      value: 'enthusiastic',
      label: 'Enthusiastic',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M494.78656 947.94752a120.34048 120.34048 0 0 1-62.13632-28.74368L103.65952 630.25152a294.8096 294.8096 0 0 1-43.85792-46.47936 294.02112 294.02112 0 0 1-43.9808-83.31264 293.03808 293.03808 0 0 1-15.29856-93.78816 293.74464 293.74464 0 0 1 13.2096-87.42912 292.07552 292.07552 0 0 1 36.97664-76.9024 294.656 294.656 0 0 1 56.7808-62.50496 296.16128 296.16128 0 0 1 46.86848-31.6416 292.42368 292.42368 0 0 1 140.0832-35.47136 293.7344 293.7344 0 0 1 165.59104 51.07712A292.15744 292.15744 0 0 1 505.1392 201.728l5.25312 5.40672 5.2736-5.40672a293.1712 293.1712 0 0 1 96.33792-65.8432 295.0656 295.0656 0 0 1 55.73632-17.2544 293.71392 293.71392 0 0 1 88.69888-4.37248A291.71712 291.71712 0 0 1 890.6752 162.9184a294.16448 294.16448 0 0 1 43.52 35.90144 294.64576 294.64576 0 0 1 62.99648 93.44 292.1472 292.1472 0 0 1 21.56544 84.31616 296.96 296.96 0 0 1-5.3248 93.44 293.86752 293.86752 0 0 1-34.78528 87.57248 294.18496 294.18496 0 0 1-61.50144 72.66304L588.15488 919.20384a121.41568 121.41568 0 0 1-27.0336 17.96096 120.56576 120.56576 0 0 1-50.71872 12.032c-5.2224-0.08192-10.4448-0.50176-15.616-1.24928z m15.60576-75.5712a44.66688 44.66688 0 0 0 13.74208-2.816 39.0144 39.0144 0 0 0 4.1472-1.83296 45.056 45.056 0 0 0 9.19552-6.22592l329.41056-289.3312a215.6544 215.6544 0 0 0 32.83968-34.7136 216.4224 216.4224 0 0 0 43.75552-130.75456 216.51456 216.51456 0 0 0-17.01888-84.5312 216.46336 216.46336 0 0 0-19.99872-36.83328 218.53184 218.53184 0 0 0-76.68736-69.66272 214.6304 214.6304 0 0 0-81.1008-25.02656 218.76736 218.76736 0 0 0-65.61792 3.23584 217.26208 217.26208 0 0 0-79.02208 33.3824 217.05728 217.05728 0 0 0-33.32096 27.99616l-60.3136 62.01344c-20.10112-20.6848-40.21248-41.34912-60.3136-62.01344a215.9616 215.9616 0 0 0-51.75296-39.28064 217.9584 217.9584 0 0 0-60.59008-22.09792 214.66112 214.66112 0 0 0-43.3152-4.36224 216.9344 216.9344 0 0 0-64.59392 9.74848 215.22432 215.22432 0 0 0-56.71936 27.27936 218.624 218.624 0 0 0-32.2048 26.5728 219.4432 219.4432 0 0 0-26.5728 32.21504 216.1664 216.1664 0 0 0-32.6144 77.45536c-1.45408 7.1168-2.54976 14.30528-3.2768 21.53472a222.4128 222.4128 0 0 0 0.14336 46.04928 216.59648 216.59648 0 0 0 75.32544 141.79328L483.328 861.4912a41.80992 41.80992 0 0 0 9.19552 6.22592 42.10688 42.10688 0 0 0 6.38976 2.62144c3.71712 1.1776 7.5776 1.85344 11.47904 2.02752z" fill="#EE5A53"/><path d="M776.41728 498.14528a25.6 25.6 0 0 1 35.6864 36.66944l-1.32096 1.28L538.3168 782.94016a25.6 25.6 0 0 1-35.6864-36.66944l1.31072-1.28 272.4864-246.84544z" fill="#EE5A53"/><path d="M844.8 455.68m-25.6 0a25.6 25.6 0 1 0 51.2 0 25.6 25.6 0 1 0-51.2 0Z" fill="#EE5A53"/></svg>
    },
    {
      value: 'formal',
      label: 'Formal',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M779.111508 524.689538c-0.261191 0.391787-0.565914 0.798084-0.870637 1.218892a3.2794 3.2794 0 0 0 0.870637-1.218892z" fill="#515151"/><path d="M515.51157 198.722952h-2.466805c-147.25378 1.334977-265.99419 122.23747-264.644703 269.520271 1.305956 145.962335 121.091131 264.717255 266.995423 264.717255h2.466806a267.096997 267.096997 0 0 0 264.731766-269.534781c-1.320466-145.962335-121.134663-264.702745-267.082487-264.702745z m-124.631722 432.721223c-0.174127-19.618359-0.92868-58.797036 24.668055-61.205798 26.946223-2.408763 57.679718-7.966331 99.891114-8.343607 26.815627-0.246681 63.367881 2.075019 100.253879 6.529779 27.831371 3.250379 27.700775 36.349105 27.918435 60.741459l-252.67344 2.278167z m271.159971-215.482719l-45.737477 128.056229c-36.944041-4.44025-73.902592-6.703907-100.384474-6.471737-42.0808 0.377276-72.828806 5.804248-99.687965 8.271054l-47.21756-127.693463c-15.221641 0.145106-28.847114-14.133345-29.021242-30.718985a31.79277 31.79277 0 0 1 31.023707-31.647664c16.962916-0.159617 30.994686 14.162366 31.154303 31.08175 0 4.846547-1.305956 9.199734-7.25531 18.863807l45.11352 50.555003 17.906106-90.807465c-12.1599-0.304723-24.435885-13.654494-24.580992-30.283666a33.026173 33.026173 0 1 1 66.052346-0.594935c0.087064 10.04135-5.107739 21.765931-14.945939 26.757585l31.589621 89.472488h0.841616l28.716519-89.965849a33.838768 33.838768 0 0 1-13.291729-26.525415 32.997152 32.997152 0 1 1 65.994304-0.594936c0.130596 15.715002-8.865989 26.249713-22.288314 30.704474l15.961683 90.502742 42.588672-50.9613a28.74554 28.74554 0 0 1-7.153736-19.110488 31.255877 31.255877 0 0 1 30.472304-31.168813c16.919384-0.159617 31.430005 14.118834 31.5606 30.61741a31.923366 31.923366 0 0 1-27.57018 31.662174z" fill="#515151"/><path d="M991.590528 73.873571L904.831527 0l-18.21083 9.156202C703.395088 101.356686 554.54514 28.571412 526.510621 13.045048l-15.221641-8.459692-15.091046 8.706373C468.511669 29.325965 321.02572 104.795703 136.232963 15.918151L117.848007 7.037651 32.409472 82.492879l10.737859 21.214528c13.422324 26.40933 57.171846 118.101943 57.795803 188.086667 0.145106 15.715002-5.194802 44.17033-11.376327 77.065907-11.042582 58.797036-24.755119 131.640352-24.102141 204.599753 1.291445 142.828041 59.493545 234.361037 172.894047 272.001588 178.161402 59.116269 237.089033 114.198586 256.330116 141.739744l25.640267 36.740892 25.045332-37.074637c18.863807-27.889413 76.90629-84.060026 254.124502-146.397653 112.747524-39.686548 169.193839-132.249798 167.931415-275.077839-0.667489-73.380209-16.324448-148.966033-27.81686-204.149924l-0.174128-1.015743c-6.645864-32.373195-12.450113-60.364183-12.580708-75.832505-0.652978-69.854129 41.500376-162.417379 54.385807-189.131431l10.346072-21.388655zM738.191557 684.582069a310.150009 310.150009 0 0 1-219.908458 93.419376h-2.902124c-170.557837 0-310.527285-138.779577-312.079922-309.366435-1.567147-172.110473 137.183409-313.42941 309.308393-314.982046h2.902124c170.572348 0 310.527285 138.779577 312.137964 309.351925a310.16452 310.16452 0 0 1-89.457977 221.57718z" fill="#515151"/></svg>
    },
    {
      value: 'humorous',
      label: 'Humorous',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64z m0 832c-212.1 0-384-171.9-384-384s171.9-384 384-384 384 171.9 384 384-171.9 384-384 384z" fill="#13227a"/><path d="M511.1 800c65.3-0.3 121.3-39.7 145.9-96h1.6c8.6-19.6 13.3-41.2 13.3-64H352c0 88.1 71.2 159.5 159.1 160z" fill="#13227a"/><path d="M416 480m-32 0a32 32 0 1 0 64 0 32 32 0 1 0-64 0Z" fill="#13227a"/><path d="M512 128c-212.1 0-384 171.9-384 384 0 128.5 63.1 242.2 160 311.9V544c0-123.2 100.8-224 224-224s224 100.8 224 224v279.9c96.9-69.7 160-183.4 160-311.9 0-212.1-171.9-384-384-384z" fill="#13227a"/><path d="M608 479.7m-32 0a32 32 0 1 0 64 0 32 32 0 1 0-64 0Z" fill="#13227a"/><path d="M416 479.7m-44 0a44 44 0 1 0 88 0 44 44 0 1 0-88 0Z" fill="#13227a"/><path d="M608 479.7m-44 0a44 44 0 1 0 88 0 44 44 0 1 0-88 0Z" fill="#13227a"/></svg>
    },
    {
      value: 'urgent',
      label: 'Urgent',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M512 4.452174C231.513043 4.452174 4.452174 231.513043 4.452174 512c0 280.486957 227.06087 507.547826 507.547826 507.547826 280.486957 0 507.547826-227.06087 507.547826-507.547826C1019.547826 231.513043 792.486957 4.452174 512 4.452174zM338.365217 556.521739H280.486957v-133.565217h57.87826v133.565217z m204.8 200.347826c-8.904348 22.26087-62.330435 22.26087-62.330434 22.26087-62.330435-80.13913-71.234783-160.278261-71.234783-160.278261h57.878261c8.904348 53.426087 57.878261 97.947826 62.330435 106.852174 17.808696 13.356522 13.356522 31.165217 13.356521 31.165217z m138.017392-80.13913s-71.234783-89.043478-307.2-102.4V405.147826s200.347826-8.904348 307.2-133.565217c0 0 89.043478-22.26087 84.591304 218.156521 0 0 8.904348 164.730435-84.591304 186.991305z" fill="#2c2c2c"/></svg>
    },
    {
      value: 'luxury',
      label: 'Luxury',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M554.666667 109.952l284.16 164.053333a85.333333 85.333333 0 0 1 42.666666 73.898667v328.149333c0 30.506667-16.256 58.666667-42.666666 73.898667L554.666667 914.048a85.376 85.376 0 0 1-85.333334 0l-284.16-164.053333a85.333333 85.333333 0 0 1-42.666666-73.898667V347.946667c0-30.506667 16.256-58.666667 42.666666-73.898667L469.333333 109.952a85.376 85.376 0 0 1 85.333334 0z" fill="#F5CF41"/><path d="M533.333333 146.901333a42.581333 42.581333 0 0 0-42.666666 0L206.506667 310.997333c-13.184 7.637333-21.333333 21.717333-21.333334 36.949334v328.149333c0 15.232 8.149333 29.312 21.333334 36.949333l284.16 164.053334c13.226667 7.637333 29.44 7.637333 42.666666 0l284.16-164.053334c13.184-7.637333 21.333333-21.717333 21.333334-36.949333V347.946667c0-15.232-8.149333-29.312-21.333334-36.949334L533.333333 146.901333z m21.333334-36.949333l284.16 164.053333a85.333333 85.333333 0 0 1 42.666666 73.898667v328.149333c0 30.506667-16.256 58.666667-42.666666 73.898667L554.666667 914.048a85.376 85.376 0 0 1-85.333334 0l-284.16-164.053333a85.333333 85.333333 0 0 1-42.666666-73.898667V347.946667c0-30.506667 16.256-58.666667 42.666666-73.898667L469.333333 109.952a85.376 85.376 0 0 1 85.333334 0z" fill="#E3A815"/><path d="M255.658667 577.237333c13.482667-19.584 6.570667-39.210667-20.736-58.794666-8.917333 33.877333-2.005333 53.504 20.736 58.794666z m20.778666 51.882667c-6.229333-24.96-24.277333-34.261333-54.186666-27.861333 9.130667 27.904 27.221333 37.205333 54.186666 27.861333z m0 0c-6.570667-29.056 3.2-47.189333 29.354667-54.314667 8.448 26.538667-1.322667 44.629333-29.354667 54.314667z m38.826667 38.229333s-18.261333-30.976-57.301333-10.368c25.856 41.856 57.301333 10.368 57.301333 10.368z m1.493333-0.085333c-12.245333-22.229333-5.973333-41.898667 18.773334-59.050667 11.264 32.042667 4.992 51.712-18.773334 59.050667z m43.477334 37.930667s-23.68-30.336-64 5.034666c42.368 30.933333 64-5.034667 64-5.034666z" fill="#FAE7A0"/><path d="M355.285333 705.152c-18.090667-17.536-17.706667-38.442667 1.237334-62.677333 18.176 26.197333 17.792 47.104-1.237334 62.677333z m52.949334 26.282667c-20.736-9.557333-25.813333-28.714667-15.232-57.429334 26.538667 15.573333 31.616 34.688 15.232 57.429334z" fill="#FAE7A0"/><path d="M408.149333 731.392c-22.016-9.557333-40.789333-1.962667-56.277333 22.869333 30.08 11.904 48.853333 4.309333 56.277333-22.869333z m360.192-154.154667c-13.482667-19.584-6.570667-39.210667 20.736-58.794666 8.917333 33.877333 2.005333 53.504-20.736 58.794666z m-20.778666 51.882667c6.229333-24.96 24.277333-34.261333 54.186666-27.861333-9.130667 27.904-27.221333 37.205333-54.186666 27.861333z m0 0c6.570667-29.056-3.2-47.189333-29.354667-54.314667-8.448 26.538667 1.322667 44.629333 29.354667 54.314667z m-38.826667 38.229333s18.261333-30.976 57.301333-10.368c-25.856 41.856-57.301333 10.368-57.301333 10.368z m0.042667-0.085333c11.306667-22.229333 5.546667-41.898667-17.322667-59.050667-10.325333 32.042667-4.565333 51.712 17.322667 59.050667z m-40.064 37.930667s21.845333-30.336 59.050666 5.034666c-39.082667 30.933333-59.050667-5.034667-59.050666-5.034666z" fill="#FAE7A0"/><path d="M668.714667 705.152c18.090667-17.536 17.706667-38.442667-1.237334-62.677333-18.176 26.197333-17.792 47.104 1.237334 62.677333z m-52.949334 26.282667c20.736-9.557333 25.813333-28.714667 15.232-57.429334-26.538667 15.573333-31.616 34.688-15.232 57.429334z" fill="#FAE7A0"/><path d="M615.850667 731.392c22.016-9.557333 40.789333-1.962667 56.277333 22.869333-30.08 11.904-48.853333 4.309333-56.277333-22.869333zM512 789.333333l-25.088 13.184 4.778667-27.904-20.266667-19.797333 28.032-4.096L512 725.333333l12.544 25.386667 28.032 4.096-20.266667 19.797333 4.778667 27.904L512 789.333333z" fill="#FAE7A0"/><path d="M360.021333 301.098667l59.946667 76.288a8.533333 8.533333 0 0 0 13.44 0L505.301333 285.866667a8.533333 8.533333 0 0 1 13.44 0l71.808 91.52a8.533333 8.533333 0 0 0 13.44 0l60.074667-76.330667a8.533333 8.533333 0 0 1 15.189333 6.272l-24.96 214.357333H369.792l-24.96-214.314666a8.533333 8.533333 0 0 1 15.189333-6.272z" fill="#FFF2A0"/><path d="M383.36 548.864h257.28c7.509333 0 13.568 6.058667 13.568 13.568v15.530667a13.568 13.568 0 0 1-13.568 13.568h-257.28a13.568 13.568 0 0 1-13.568-13.568v-15.530667c0-7.509333 6.058667-13.568 13.568-13.568z" fill="#FBE975"/></svg>
    },
    {
      value: 'technical',
      label: 'Technical',
      icon: <svg className="w-4 h-4" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg"><path d="M803.4 963c-32.8 0-65.2-12.8-89.2-36.6-36-36-46.4-90-27.6-136.4L293.2 396.4c-46.2 18.8-100.4 8.4-136.4-27.6-36.4-36.4-47-92.8-26-139.8 2.6-6 8.2-10.4 14.6-11.6 6.4-1.2 13.2 0.8 17.8 5.6l46.6 46.6 38.4-38.4-46.6-46.6c-4.6-4.6-6.8-11.4-5.6-17.8 1.2-6.4 5.6-12 11.6-14.6 47.2-21 103.4-10.4 139.8 26 36 36 46.4 90 27.6 136.4L768.8 708c46.2-18.8 100.4-8.4 136.4 27.6 36.4 36.4 47 92.8 26 139.8-2.6 6-8.2 10.4-14.6 11.6-6.4 1.2-13.2-0.8-17.8-5.6L852 834.8l-38.4 38.4 46.6 46.6c4.6 4.6 6.8 11.4 5.6 17.8-1.2 6.4-5.6 12-11.6 14.6-16.2 7.2-33.6 10.8-50.8 10.8z m-506-610.6c5.2 0 10.2 2 14.2 5.8l413.6 413.6c6.4 6.4 7.6 16 3.4 24-18.6 33.2-12.8 75.4 14.2 102.4 17.2 17.2 40.6 25.8 64.2 25l-35.4-35.4c-3.8-3.8-5.8-8.8-5.8-14.2s2.2-10.4 5.8-14.2l66.8-66.8c7.8-7.8 20.4-7.8 28.2 0L902 828c1-23.4-7.8-47-25-64.2-27-27-69.2-32.8-102.4-14.2-7.8 4.4-17.6 3-24-3.4L337 332.8c-6.4-6.4-7.6-16-3.4-24 18.6-33.2 12.8-75.4-14.2-102.4-17.2-17.2-40.6-25.8-64.2-25l35.4 35.4c3.8 3.8 5.8 8.8 5.8 14.2s-2.2 10.4-5.8 14.2L224 312c-3.8 3.8-8.8 5.8-14.2 5.8-5.4 0-10.4-2.2-14.2-5.8l-35.4-35.4c-1 23.4 7.8 47 25 64.2 27 27 69.2 32.8 102.4 14.2 3-1.8 6.4-2.6 9.8-2.6z" fill="#13227a"/><path d="M218.6 950.2c-5.4 0-10.4-2.2-14.2-5.8L139 879c-3.8-3.8-5.8-8.8-5.8-14.2s2.2-10.4 5.8-14.2l262.4-262.4c7.8-7.8 20.4-7.8 28.2 0 7.8 7.8 7.8 20.4 0 28.2l-248 248.4 37 37 248.2-248.2c7.8-7.8 20.4-7.8 28.2 0 7.8 7.8 7.8 20.4 0 28.2L232.8 944.4c-3.8 3.6-9 5.8-14.2 5.8zM648.2 516c-5.2 0-10.2-2-14.2-5.8-7.8-7.8-7.8-20.4 0-28.2l188.2-188.2c6.6-6.6 17.2-7.8 25-2.6 0.6 0.4 15.8 10.4 37.2 31.8 12 12 25.2 31.4 37 51.2-2.6-33.4-9.6-72.4-26-105.8-27-54.8-102.6-129.6-134-159.4l-48 48 71.4 71.4c7.8 7.8 7.8 20.4 0 28.2L596.6 444.8c-7.8 7.8-20.4 7.8-28.2 0-7.8-7.8-7.8-20.4 0-28.2l174-174-71.4-71.4c-7.8-7.8-7.8-20.4 0-28.2l75.6-75.6c7.6-7.6 19.6-7.8 27.6-0.8C779 71 893.6 174.4 931.2 251c42.8 86.8 30 199.6 29.4 204.4-1 8.8-7.8 16-16.6 17.4-8.8 1.4-17.4-3.2-21.4-11.2-10.8-23-44-87.6-66.6-110-7-7-13.2-12.6-18-16.6L662.4 510c-4 4-9 6-14.2 6z" fill="#13227a"/></svg>
    }
  ];

  const languages = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'en-CA', label: 'English (Canada)' },
    { value: 'en-AU', label: 'English (Australia)' },
    { value: 'zh-CN', label: '简体中文 (Simplified Chinese)' },
    { value: 'zh-TW', label: '繁體中文 (Traditional Chinese)' },
    { value: 'ja-JP', label: '日本語 (Japanese)' },
    { value: 'ko-KR', label: '한국어 (Korean)' },
    { value: 'es-ES', label: 'Español (Spanish)' },
    { value: 'fr-FR', label: 'Français (French)' },
    { value: 'de-DE', label: 'Deutsch (German)' },
    { value: 'it-IT', label: 'Italiano (Italian)' },
    { value: 'pt-BR', label: 'Português (Portuguese)' },
    { value: 'ru-RU', label: 'Русский (Russian)' },
    { value: 'ar-SA', label: 'العربية (Arabic)' },
    { value: 'hi-IN', label: 'हिन्दी (Hindi)' },
    { value: 'th-TH', label: 'ไทย (Thai)' },
    { value: 'vi-VN', label: 'Tiếng Việt (Vietnamese)' },
    { value: 'id-ID', label: 'Bahasa Indonesia (Indonesian)' },
    { value: 'nl-NL', label: 'Nederlands (Dutch)' },
    { value: 'pl-PL', label: 'Polski (Polish)' },
    { value: 'tr-TR', label: 'Türkçe (Turkish)' }
  ];

  // Load prefilled data from Workspace if available
  useEffect(() => {
    if (location.state?.prefillData) {
      const prefillData = location.state.prefillData;

      setFormData(prev => ({
        ...prev,
        productName: prefillData.productName || prev.productName,
        productDescription: prefillData.productDescription || prev.productDescription,
        targetAudience: prefillData.targetAudience || prev.targetAudience,
        tone: prefillData.tone || prev.tone,
        language: prefillData.language || prev.language,
        keywords: prefillData.keywords || prev.keywords,
        cta: prefillData.cta || prev.cta,
        brandVoice: prefillData.brandVoice || prev.brandVoice,
        painPoints: prefillData.painPoints || prev.painPoints,
        benefits: prefillData.benefits || prev.benefits,
        competitors: prefillData.competitors || prev.competitors,
        numVariations: prefillData.numVariations || prev.numVariations,
        maxLength: prefillData.maxLength || prev.maxLength
      }));

      // If advanced fields exist, enable advanced mode
      if (prefillData.keywords || prefillData.cta || prefillData.brandVoice ||
          prefillData.painPoints || prefillData.benefits || prefillData.competitors) {
        setIsAdvancedMode(true);
      }

      // If previous variations exist, display them
      if (prefillData.previousVariations && prefillData.previousVariations.length > 0) {
        setGeneratedAds(prefillData.previousVariations);
        setActiveTab(0);
      }
    }
  }, [location.state]);

  // Load available models on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await contentAPI.getAvailableModels();
        const models = response.data.data;
        // Filter out doubao-seed-1-6 model
        const filteredModels = models.filter(m => m.name !== 'doubao-seed-1-6');
        setAvailableModels(filteredModels);
        if (filteredModels.length > 0 && !selectedModel) {
          const defaultModel = filteredModels.find(m => m.name === 'deepseek-chat') || filteredModels[0];
          setSelectedModel(defaultModel);
        }
      } catch (error) {
        console.error('Failed to load models:', error);
      }
    };
    loadModels();
  }, [selectedModel]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOptimizeDescription = async () => {
    if (!formData.productDescription.trim()) {
      return;
    }

    setOptimizingDescription(true);

    try {
      const response = await contentAPI.optimizeAdDescription({
        description: formData.productDescription.trim()
      });

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          productDescription: response.data.data.optimizedDescription
        }));
      }
    } catch (err) {
      console.error('Description optimization error:', err);
    } finally {
      setOptimizingDescription(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    // Check model permission before generating
    if (selectedModel && !hasModelAccess(selectedModel.name)) {
      const modelInfo = getModelDisplayInfo(selectedModel.name);
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'model',
        featureName: modelInfo.displayName,
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForModel(selectedModel.name)
      });
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);
    setGeneratedAds([]);

    try {
      // Call backend API to generate ad copy
      const response = await contentAPI.generateAdCopy({
        ...formData,
        model_id: selectedModel?.id
      });

      setGeneratedAds(response.data.data.variations || []);
      setActiveTab(0);
    } catch (error) {
      console.error('Failed to generate ad copy:', error);
      alert(error.response?.data?.error?.message || 'Failed to generate ad copy. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const handleSaveAd = async (ad) => {
    // TODO: Implement save to history
    console.log('Saving ad:', ad);
  };

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <div className="flex h-screen bg-gray-50">
        <Sidebar />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <div className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <svg className="w-12 h-12" viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
                    <path d="M64 512c0 160.055 85.388 307.952 224 387.98 138.612 80.027 309.388 80.027 448 0C874.612 819.951 960 672.054 960 512c0-247.424-200.576-448-448-448S64 264.576 64 512z" fill="#E78AF2" />
                    <path d="M742.648 260H271.954c-16.94 0-30.62 13.775-30.62 30.69V637.37c0 13.263 10.675 23.962 23.908 23.962h477.47c16.941 0 30.621-13.775 30.621-30.69V290.69c0-16.978-13.744-30.689-30.685-30.689zM598.68 465.115L466.974 538.22c-8.946 5.005-20.307-1.14-20.307-10.959V384.726c0-9.692 11.035-15.837 20.046-11.086l131.706 69.43c9.076 4.751 9.272 17.04 0.261 22.045zM707.367 764H316.633c-0.38 0-0.633-0.254-0.633-0.635v-64.063c0-0.318 0.253-0.635 0.633-0.635h390.734c0.317 0 0.633 0.254 0.633 0.635v64.063c-0.063 0.381-0.316 0.635-0.633 0.635z" fill="#F9EFFF" />
                  </svg>
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">
                      Ad Copy Generator
                    </h1>
                    <p className="text-gray-600 mt-1">
                      Create compelling advertising content for all major platforms with AI
                    </p>
                  </div>
                </div>

                {/* Mode Toggle */}
                <div className="flex items-center space-x-3 bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200">
                  <span className={`text-sm font-medium ${!isAdvancedMode ? 'text-primary-600' : 'text-gray-500'}`}>
                    Simple
                  </span>
                  <button
                    onClick={() => setIsAdvancedMode(!isAdvancedMode)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
                      isAdvancedMode ? 'bg-primary-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                        isAdvancedMode ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-sm font-medium ${isAdvancedMode ? 'text-primary-600' : 'text-gray-500'}`}>
                    Advanced
                  </span>
                </div>
              </div>

              {/* Model Selector */}
              <div className="flex items-center space-x-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span className="text-sm text-blue-900 font-medium">AI Model:</span>
                <select
                  value={selectedModel?.id || ''}
                  onChange={(e) => {
                    const model = availableModels.find(m => m.id === e.target.value);
                    setSelectedModel(model);
                  }}
                  className="flex-1 max-w-xs px-3 py-2 pr-8 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium hover:bg-gray-50 transition cursor-pointer appearance-none bg-no-repeat bg-right"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundSize: '1.25rem',
                    backgroundPosition: 'right 0.5rem center'
                  }}
                >
                  {availableModels.length === 0 && (
                    <option value="">Loading models...</option>
                  )}
                  {availableModels.map(model => {
                    const modelInfo = getModelDisplayInfo(model.name);
                    const hasAccess = hasModelAccess(model.name);
                    return (
                      <option key={model.id} value={model.id} disabled={!hasAccess}>
                        {modelInfo.icon} {modelInfo.displayName} {!hasAccess ? '🔒' : ''}
                      </option>
                    );
                  })}
                </select>

                {/* Model Badge */}
                {selectedModel && (
                  <div className="flex items-center">
                    {(() => {
                      const modelInfo = getModelDisplayInfo(selectedModel.name);
                      const badgeColors = {
                        'Basic': 'bg-gray-100 text-gray-700 border-gray-300',
                        'Pro': 'bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700 border-purple-300'
                      };
                      if (!modelInfo.badge) return null;
                      return (
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${badgeColors[modelInfo.badge] || 'bg-gray-100 text-gray-700 border-gray-300'}`}>
                          {modelInfo.badge}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Campaign Details</h2>

                <form onSubmit={handleGenerate} className="space-y-6">
                  {/* Product Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Product/Service Name *
                    </label>
                    <input
                      type="text"
                      name="productName"
                      value={formData.productName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., SmartWatch Pro, Premium Coffee Beans"
                      required
                      onInvalid={(e) => e.target.setCustomValidity('Please enter a product or service name')}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                  </div>

                  {/* Product Description */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Product Description *
                      </label>
                      {formData.productDescription.trim() && (
                        <button
                          type="button"
                          onClick={handleOptimizeDescription}
                          disabled={optimizingDescription}
                          className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full hover:from-purple-600 hover:to-pink-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {optimizingDescription ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Optimizing...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span>AI Optimize</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <textarea
                      name="productDescription"
                      value={formData.productDescription}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Describe your product, its features, and what makes it unique..."
                      required
                      onInvalid={(e) => e.target.setCustomValidity('Please enter a product description')}
                      onInput={(e) => e.target.setCustomValidity('')}
                    ></textarea>
                  </div>

                  {/* Target Audience */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Audience *
                    </label>
                    <input
                      type="text"
                      name="targetAudience"
                      value={formData.targetAudience}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="e.g., Tech-savvy millennials aged 25-40, Small business owners"
                      required
                      onInvalid={(e) => e.target.setCustomValidity('Please enter your target audience')}
                      onInput={(e) => e.target.setCustomValidity('')}
                    />
                  </div>

                  {/* Advanced Mode Fields */}
                  {isAdvancedMode && (
                    <div className="space-y-6 pt-6 border-t border-gray-200">
                      <h3 className="text-md font-semibold text-gray-900 flex items-center">
                        <svg className="w-5 h-5 mr-2 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                        </svg>
                        Advanced Options
                      </h3>

                      {/* Keywords */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Target Keywords (comma-separated)
                        </label>
                        <input
                          type="text"
                          name="keywords"
                          value={formData.keywords}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="e.g., smartwatch, fitness tracker, health monitor"
                        />
                      </div>

                      {/* Call-to-Action */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Preferred Call-to-Action
                        </label>
                        <input
                          type="text"
                          name="cta"
                          value={formData.cta}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="e.g., Shop Now, Learn More, Get Started"
                        />
                      </div>

                      {/* Brand Voice */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Voice Guidelines
                        </label>
                        <textarea
                          name="brandVoice"
                          value={formData.brandVoice}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Describe your brand's personality and communication style..."
                        ></textarea>
                      </div>

                      {/* Pain Points */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Customer Pain Points
                        </label>
                        <textarea
                          name="painPoints"
                          value={formData.painPoints}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="What problems does your product solve?"
                        ></textarea>
                      </div>

                      {/* Benefits */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Key Benefits
                        </label>
                        <textarea
                          name="benefits"
                          value={formData.benefits}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="List the main benefits your customers will get..."
                        ></textarea>
                      </div>

                      {/* Competitors */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Key Competitors (optional)
                        </label>
                        <input
                          type="text"
                          name="competitors"
                          value={formData.competitors}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="e.g., Apple Watch, Fitbit, Garmin"
                        />
                      </div>
                    </div>
                  )}

                  {/* Tone Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Tone of Voice
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {tones.map(tone => (
                        <button
                          key={tone.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, tone: tone.value }))}
                          className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition ${
                            formData.tone === tone.value
                              ? 'border-primary-500 bg-primary-50 text-primary-700'
                              : 'border-gray-200 text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          <span className="mr-1 inline-flex items-center">{tone.icon || tone.emoji}</span>
                          {tone.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Language Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ad Language
                    </label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      {languages.map(lang => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Generation Options */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Variations
                      </label>
                      <select
                        name="numVariations"
                        value={formData.numVariations}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="1">1 variation</option>
                        <option value="2">2 variations</option>
                        <option value="3">3 variations</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Length
                      </label>
                      <select
                        name="maxLength"
                        value={formData.maxLength}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="short">Short (500 chars)</option>
                        <option value="medium">Medium (1000 chars)</option>
                        <option value="long">Long (1500 chars)</option>
                      </select>
                    </div>
                  </div>

                  {/* Generate Button */}
                  <button
                    type="submit"
                    disabled={generating}
                    className="w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-secondary-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {generating ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Generating Ad Variations...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Generate Ad Copy
                      </span>
                    )}
                  </button>
                </form>
              </div>

              {/* Output Area */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Generated Ad Variations</h2>
                  {generatedAds.length > 0 && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                      {generatedAds.length} {generatedAds.length === 1 ? 'variant' : 'variants'}
                    </span>
                  )}
                </div>

                {generatedAds.length > 0 ? (
                  <div className="space-y-4">
                    {/* Tabs for variations */}
                    {generatedAds.length > 1 && (
                      <div className="flex space-x-2 overflow-x-auto pb-2">
                        {generatedAds.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setActiveTab(index)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition flex-shrink-0 ${
                              activeTab === index
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Variant {index + 1}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Active variation content */}
                    {generatedAds[activeTab] && (
                      <div className="space-y-4">
                        {/* Ad Copy */}
                        <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm relative pb-16">
                          {/* Headline */}
                          {generatedAds[activeTab].headline && (
                            <div className="mb-6">
                              <h3 className="text-2xl font-bold text-gray-900 leading-tight">{generatedAds[activeTab].headline}</h3>
                            </div>
                          )}

                          {/* Description */}
                          {generatedAds[activeTab].description && (
                            <div className="mb-6">
                              <p className="text-base text-gray-700 leading-relaxed">{generatedAds[activeTab].description}</p>
                            </div>
                          )}

                          {/* Call-to-Action */}
                          {generatedAds[activeTab].cta && (
                            <div className="pt-2 mb-4">
                              <p className="text-base text-primary-600 font-semibold">{generatedAds[activeTab].cta}</p>
                            </div>
                          )}

                          {/* Copy All Button - Bottom Right */}
                          <button
                            onClick={() => handleCopyToClipboard(
                              [
                                generatedAds[activeTab].headline,
                                generatedAds[activeTab].description,
                                generatedAds[activeTab].cta
                              ].filter(Boolean).join('\n\n')
                            )}
                            className={`absolute bottom-4 right-4 flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition rounded-lg border ${
                              copied
                                ? 'text-green-600 bg-green-50 border-green-200'
                                : 'text-gray-600 hover:text-primary-600 bg-gray-50 hover:bg-primary-50 border-gray-200'
                            }`}
                          >
                            {copied ? (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Copied!
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copy All
                              </>
                            )}
                          </button>

                          {/* Full Copy (if no structured format) */}
                          {!generatedAds[activeTab].headline && !generatedAds[activeTab].description && generatedAds[activeTab].copy && (
                            <div className="mt-2">
                              <pre className="whitespace-pre-wrap text-base text-gray-800 font-sans leading-relaxed">
                                {generatedAds[activeTab].copy}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96 text-gray-400">
                    <svg className="w-20 h-20 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                    <p className="text-lg font-medium mb-2">Ready to Create Amazing Ads</p>
                    <p className="text-sm text-center max-w-md">
                      Fill in your campaign details on the left and click "Generate Ad Copy" to create compelling advertising content with AI
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />
      </div>
    </>
  );
};

export default AdCopy;
